/**
 * Where the phone is — for the proof taken with a photo (a meeting's selfie, a
 * showroom's board, a week's stock photos) and for the attendance mark.
 *
 * Owner, 28 Oct 2026: "Awdesh is trying to login meeting app isnt taking
 * location." The first reading asked for a precise, fresh fix within ten
 * seconds and gave up there, counting the time the ASM spent on the browser's
 * own "Allow location?" question; indoors nothing fell back to the phone's
 * rough place (Wi-Fi and towers); and every miss said "Location is off".
 *
 * Now:
 *   - Where the browser may still ask its question, the first ask is a rough
 *     one given time to answer it — Chrome counts the question against the
 *     timeout again since 142 — and a precise one follows, once allowed; the
 *     rough place stands if no precise one comes.
 *   - Where location is already allowed, precise first, then rough.
 *   - A miss says which: refused, not found, or a browser that cannot share
 *     one. A refusal is not told apart further — Chrome on Android folds the
 *     phone's own Location switch into what it says of the site, and Safari
 *     before 26 says "prompt" after nearly every refusal (review) — so the
 *     advice walks the checks in order instead.
 */
import type { GeoPoint } from '@bright/shared';

/** Why there is no reading. */
export type LocationMiss = 'refused' | 'not-found' | 'unsupported';

export interface LocationRead {
  point: GeoPoint | null;
  miss: LocationMiss | null;
}

export interface LocationAsk {
  /** How old the precise reading may be — none for proof: a cached place is where the phone was before. */
  maximumAgeMs: number;
  /** How long the precise reading may take. */
  preciseMs: number;
  /** The rough reading (Wi-Fi and towers): how long it may take — 0 for none. */
  roughMs: number;
  /** How old the rough reading may be. */
  roughAgeMs: number;
}

/** Proof taken now, with a photo: precise and fresh if it can be, else rough and at most half a minute old. */
export const PROOF_ASK: LocationAsk = { maximumAgeMs: 0, preciseMs: 12_000, roughMs: 15_000, roughAgeMs: 30_000 };

/** Time to answer the browser's question about location, when it asks one. */
const ANSWERING_MS = 45_000;
/** A little past the browser's own timeout, for a browser that never calls back. */
const SLACK_MS = 1_500;

const PERMISSION_DENIED = 1;

function pointOf(position: GeolocationPosition): GeoPoint {
  return {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    accuracy: Number.isFinite(position.coords.accuracy) ? Math.round(position.coords.accuracy) : null,
  };
}

/** What the browser says of location for this site, when it will say — within a second, or not at all. */
async function permissionOf(): Promise<PermissionState | null> {
  try {
    if (!navigator.permissions?.query) return null;
    const query = navigator.permissions.query({ name: 'geolocation' as PermissionName }).then((status) => status.state);
    return await Promise.race([query, new Promise<null>((resolve) => setTimeout(() => resolve(null), 1_000))]);
  } catch {
    return null;
  }
}

/** One ask of the browser; `code` is its error's (1 refused, 2 unavailable, 3 timeout), null on a reading. */
function askOnce(options: PositionOptions & { timeout: number }): Promise<{ point: GeoPoint | null; code: number | null }> {
  return new Promise((resolve) => {
    let settled = false;
    let guard: ReturnType<typeof setTimeout> | null = null;
    const finish = (value: { point: GeoPoint | null; code: number | null }) => {
      if (settled) return;
      settled = true;
      if (guard !== null) clearTimeout(guard);
      resolve(value);
    };
    guard = setTimeout(() => finish({ point: null, code: 3 }), options.timeout + SLACK_MS);
    try {
      navigator.geolocation.getCurrentPosition(
        (position) => finish({ point: pointOf(position), code: null }),
        (error) => finish({ point: null, code: error.code }),
        options,
      );
    } catch {
      finish({ point: null, code: 2 });
    }
  });
}

const refused: LocationRead = { point: null, miss: 'refused' };
const notFound: LocationRead = { point: null, miss: 'not-found' };

/**
 * Where the phone is, or why it will not say. Asked even where the browser
 * already says "denied": on Android that can be the phone's switch, and an ask
 * made on a tap is what lets Chrome offer to turn it on.
 */
export async function readLocation(ask: LocationAsk): Promise<LocationRead> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return { point: null, miss: 'unsupported' };
  // A page not served over https is never given a location.
  if (typeof window !== 'undefined' && window.isSecureContext === false) return { point: null, miss: 'unsupported' };
  const allowed = (await permissionOf()) === 'granted';
  const precise = (extraMs: number) => askOnce({ enableHighAccuracy: true, timeout: ask.preciseMs + extraMs, maximumAge: ask.maximumAgeMs });
  const rough = (extraMs: number) => askOnce({ enableHighAccuracy: false, timeout: ask.roughMs + extraMs, maximumAge: ask.roughAgeMs });

  // One quick ask only (the attendance mark), with time to answer the question if there is one.
  if (ask.roughMs <= 0) {
    const only = await precise(allowed ? 0 : ANSWERING_MS);
    return only.point ? { point: only.point, miss: null } : only.code === PERMISSION_DENIED ? refused : notFound;
  }

  if (allowed) {
    const first = await precise(0);
    if (first.point) return { point: first.point, miss: null };
    if (first.code === PERMISSION_DENIED) return refused;
    const second = await rough(0);
    if (second.point) return { point: second.point, miss: null };
    return second.code === PERMISSION_DENIED ? refused : notFound;
  }

  // The question may be on screen: a rough ask that waits for the answer, then a precise one.
  const first = await rough(ANSWERING_MS);
  if (first.code === PERMISSION_DENIED) return refused;
  const second = await precise(0);
  if (second.point) return { point: second.point, miss: null };
  if (first.point) return { point: first.point, miss: null };
  return second.code === PERMISSION_DENIED ? refused : notFound;
}

/** The browser in hand on an iPhone or iPad, by the name its settings are under; null elsewhere. */
export function appleBrowser(): 'Safari' | 'Chrome' | 'Firefox' | 'Edge' | null {
  if (typeof navigator === 'undefined') return null;
  const agent = navigator.userAgent ?? '';
  // iPadOS asks as a Mac; a Mac with a touch screen is one.
  const apple = /iPhone|iPad|iPod/.test(agent) || (/Macintosh/.test(agent) && (navigator.maxTouchPoints ?? 0) > 1);
  if (!apple) return null;
  if (/CriOS/.test(agent)) return 'Chrome';
  if (/FxiOS/.test(agent)) return 'Firefox';
  if (/EdgiOS/.test(agent)) return 'Edge';
  return 'Safari';
}

/**
 * A page opened inside another app's own browser (an Android WebView — a link
 * tapped in an app that keeps it in-house), not the phone app's shell: there
 * the location is the host app's to give, and it rarely is.
 */
export function inAnotherAppsBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const agent = navigator.userAgent ?? '';
  const shell = typeof window !== 'undefined' && Boolean((window as unknown as { Capacitor?: unknown }).Capacitor);
  return !shell && /; wv\)/.test(agent);
}
